import React from 'react'
import { List } from 'immutable'

import debounce from 'lodash.debounce'

import Button from 'shared-web/src/components/Button'
import { getDisplayName } from 'shared-web/src/helpers/CustomerHelpers'
import UserSelect from 'components/UserSelect'
import WelcomeMessageBuilder from 'components/shared/MessagePreviews/WelcomeMessage/WelcomeMessageBuilder'
import { CtaTypes } from 'app/modules/playbook/constants'

import Editor from '../Editor'
import Card from '../Card'
import MessageCta from '../MessageCta'

import OutboundSendFrom from './OutboundSendFrom'
import ABMSendFrom from './ABMSendFrom'

import { Icon, Input, ToolTip } from '@driftt/tide-core-legacy'
import cx from 'classnames'

import { getIframelyVideoEmbed } from 'app/modules/conversation/api'

import './style.styl'
import { isURL } from 'validator'
import { isNil } from 'ramda'


const fetchVideoEmbed = (videoUrlInput, { setVideoUrl, setVideoEmbed }) => {
  return getIframelyVideoEmbed(videoUrlInput)
    .then((data) => {
      // only persist valid video url's to redux
      if (data.isValid) {
        setVideoUrl(data.url)
      }
      else {
        setVideoUrl(null)
      }

      setVideoEmbed(!isNil(data.embed) ? data.embed : null)
    })
}

const validateUrl = async (videoUrlInput, { setVideoUrlInput, setVideoUrl, setVideoEmbed }) => {
  // only perform check if url has changed
  const urlIsEligibleForEmbedCheck = isURL(videoUrlInput)

  if (urlIsEligibleForEmbedCheck) {
    try {
      await fetchVideoEmbedDebounced(videoUrlInput, { setVideoUrl, setVideoEmbed })
    }
    catch(e) {
      console.warn(`Failed to fetch embed for ${videoUrlInput}`)
    }
  } else {
    // reset video url in redux because the current input is invalid
    setVideoUrl(null)
    setVideoEmbed(null)
  }

  // set local state of video url input text
  setVideoUrlInput(videoUrlInput)
}


const handleVideoUrlChange = (event, handlers) => {
  const value = event.target.value
  validateUrl(value, handlers)
}

const fetchVideoEmbedDebounced = debounce((value, handlers) => fetchVideoEmbed(value, handlers), 1000)

const WelcomeMessage = ({
  backgroundColor,
  foregroundColor,
  selectedUser,
  message,
  onMessageChange,
  videoEmbed,
  videoUrlInput,
  setVideoUrlInput,
  videoCutoverStatus,
  onUserIdChange,
  cta,
  isOutboundPlaybook,
  isABMPlaybook,
  usersWithExtension,
  isOutreachConnected,
  setVideoEmbed,
  actions: { showExtensionInviteModal, setVideoUrl },
}) => {
  const sendFromComponent = () => (
    isABMPlaybook ?
    <ABMSendFrom /> :
    <OutboundSendFrom
      isOutreachConnected={isOutreachConnected}
      usersWithExtension={usersWithExtension}
      onEditClick={showExtensionInviteModal}
    />
  )

  const isVideoUrlInputValid = () => !isNil(videoEmbed) || videoUrlInput === ''

  const buildDescription = () => {
    return (
      <div>
        {!isVideoUrlInputValid() && <span className={'video-url-input-description-invalid'} >Whoops, looks like you've added an invalid link!</span>}
        <div>
          <span className={'video-url-input-description left'}>Embed a video in your welcome message to engage site visitors!</span>
          <ToolTip
            flow="up"
            component={<span>We support video URLs from Youtube, Vimeo, ViewdIt, Vidyard, Vidyard GoVideo, Wistia, Giphy, thehippovideo, and Hyfy</span>}
            className="overwrite-info-button">
            <Icon className={'video-url-input-description tooltip-icon'} width={15} height={15} name="info"/>
          </ToolTip>
        </div>
      </div>
    )
  }

  const videoEmbedWithTitle = `${message} ${(!isNil(videoEmbed) ? videoEmbed : '')}`

  const handleVideoUrlChangeEvent = (event) =>
    handleVideoUrlChange(event, { setVideoUrlInput, setVideoUrl, setVideoEmbed, videoUrlInput })

  return (
    <div className="playbook-message">
      <header className="playbook-title-header">
        <h2>
          {(isOutboundPlaybook || isABMPlaybook)
            ? 'Customize the message your users will see.'
            : 'Create a Welcome Message'}
        </h2>
        <p>What do you want to say? Keep it short and sweet, be personal and human!</p>
      </header>
      <Card title="Welcome Message" className="drift-form" hideDescription>
        <div className="playbook-message-preview">
          <WelcomeMessageBuilder
            backgroundColor={backgroundColor}
            foregroundColor={foregroundColor}
            showProfileIcon
            users={List.of(selectedUser)}
            title={getDisplayName(selectedUser)}
            message={videoEmbedWithTitle}
            isHTMLPreview={true}
          >
            {cta.type === CtaTypes.CHAT &&
              <div className="playbook-welcome-message-composer">Type your message...</div>}
            {(cta.type === CtaTypes.MEETING || cta.type === CtaTypes.URL) &&
              <Button
                className="playbook-welcome-message-button"
                block
                hollow
                brandPrimary={backgroundColor}
                style={{ borderWidth: '0px' }}
              >
                {cta.copy}
              </Button>}
          </WelcomeMessageBuilder>
        </div>
        <div className="playbook-edit-message-heading">Welcome Message Copy</div>
        <Editor value={message} onChange={onMessageChange} />
        {videoCutoverStatus &&
        <Input
          placeholder="eg. https://www.youtube.com/watch?v=3028oDEKZo4"
          label="Video URL (optional)"
          value={videoUrlInput}
          type="text"
          className={cx('playbook-edit-message-heading', { 'video-url-input-invalid' : isVideoUrlInputValid() })}
          onChange={event => handleVideoUrlChangeEvent(event)}
          size="large"
          description={buildDescription()}
        />
        }
        <div className="playbook-edit-message-heading">Send From</div>
        {(isOutboundPlaybook || isABMPlaybook)
          ? sendFromComponent()
          : <UserSelect
              selectedUserId={selectedUser ? selectedUser.id : null}
              includeRandom={true}
              restrictCalendarUsers={cta && cta.type === CtaTypes.MEETING ? false : ((cta.type === CtaTypes.CHAT) ? true : false)}
              excludeBot={cta && cta.type === CtaTypes.MEETING}
              onUserIdChange={onUserIdChange}
            />}
        {!isOutboundPlaybook && <MessageCta hideCopy={cta.type === CtaTypes.CHAT} isABMPlaybook={isABMPlaybook} />}
      </Card>
    </div>
  )
}

export default WelcomeMessage



// WEBPACK FOOTER //
// ./src/scripts/components/playbook/components/WelcomeMessage/component.js